import React, { useEffect, useState } from 'react';
import { Events, publish } from '@conversion-cloud-widget/pubsub';
import { ChatBubble, Content, PrimaryButton, TypingBubble } from '@conversion-cloud-widget/UI';
import { openLiveChat } from '../../utils';

const Dialog = ({ chatBot, companyId, greeting, handleKeyDown, topics, brandPrimaryColor }) => {
    /* Local state to track history of chat dialog */
    const [history, setHistory] = useState([]);

    /* Delay to simulate typing/thinking */
    const typingDelay = 250;

    /* Quick & Dirty way to generate random keys without importing a library */
    const generateKey = prefix => {
        const key = Math.random().toString(32).substring(2, 24);
        return `${prefix}-${key}`;
    };

    /* Inline, reusable elements */
    const TopicsButton = () => {
        return (
            <PrimaryButton
                key={generateKey('another-topic')}
                label={'Pick another topic'}
                onClick={() => showTopics(true)}
                onKeyDown={() => showTopics(true)}
            />
        );
    };
    const TopicsList = () => {
        return (
            <div style={{ marginTop: '8px', overflowY: 'auto' }}>
                {topics.map(topic => {
                    if (!topic.display_name || (!topic.action && topic.behavior !== 'open_sg') || !topic.enabled) {
                        return;
                    }
                    return (
                        <div style={{ marginBottom: '8px' }} key={generateKey(`topic-button-wrapper-${topic.id}`)}>
                            <PrimaryButton
                                className="full"
                                key={generateKey(`topic-button-${topic.id}`)}
                                label={topic.display_name}
                                onClick={() => handleTopicClick(topic)}
                                onKeyDown={() => handleKeyDown(topic)}
                            />
                        </div>
                    );
                })}
            </div>
        );
    };

    /* Components map for dynamic rendering */
    const componentsMap = {
        response: ChatBubble,
        topicsButton: TopicsButton,
        topicList: TopicsList,
        typing: TypingBubble
    };

    const scrollChatWindow = () => {
        const chatContentContainer = document.getElementById('clx-chatbot-dialog-window');
        const chatBotWindow = document.querySelector('.clx-step__content');

        chatBotWindow.scroll({
            top: chatContentContainer.scrollHeight + (chatContentContainer.offsetHeight - chatBotWindow.scrollHeight),
            behavior: 'smooth'
        });
    };

    const updateHistory = newEntry => {
        setHistory(existingHistory => {
            return [...existingHistory, newEntry];
        });
    };

    const showResponse = (content, header = '') => {
        const typingBubble = { type: 'typing' };
        updateHistory(typingBubble);
        scrollChatWindow();

        setTimeout(() => {
            if (header !== '') {
                const topicHeader = {
                    type: 'response',
                    content: header
                };
                updateHistory(topicHeader);
                scrollChatWindow();
            }
            const topicResponse = {
                type: 'response',
                content: content
            };
            updateHistory(topicResponse);
            scrollChatWindow();
        }, typingDelay);

        setTimeout(() => {
            setHistory(existingHistory => {
                return [...existingHistory].filter(item => item.type !== 'typing');
            });
        }, typingDelay);
    };

    const showTopics = (delay = false) => {
        const topicsResponse = {
            type: 'topicList'
        };

        if (delay) {
            setTimeout(() => {
                updateHistory(topicsResponse);
                scrollChatWindow();
            }, typingDelay);

            setTimeout(() => {
                setHistory(existingHistory => {
                    return [...existingHistory].filter(item => item.type !== 'typing');
                });
            }, typingDelay);
        } else {
            updateHistory(topicsResponse);
        }
    };

    const showAnotherTopicButton = () => {
        setTimeout(() => {
            updateHistory({
                type: 'topicsButton'
            });
        }, typingDelay);
    };

    const startDialog = () => {
        showResponse(greeting);
        setTimeout(() => {
            showTopics();
        }, typingDelay);
    };
    const saveChatBotInteraction = (value)=>{
        const interactions =localStorage.getItem("chatBotInteraction")
        localStorage.setItem('chatBotInteraction',interactions ? `${interactions},${value}` : value)
    }
    const handleTopicClick = topic => {
        const { action, behavior } = topic;
        saveChatBotInteraction(topic.display_name)
        publish(Events.GA_EVENT, {
            action: `Topic ${topic.id} Clicked`,
            module: process.env.MODULE_NAME,
            category: 'TCC'
        });

        publish(Events.CHATBOT_INTERACTED, {
            module: process.env.MODULE_NAME,
            companyId: companyId,
            data: {
                id: topic.id,
                name: topic.name,
                action: topic.action,
                behavior: topic.behavior
            }
        });

        switch (behavior) {
            case 'navigate_link':
                window.location = action;
                break;
            case 'open_sg':
                publish(Events.MODULE_CLOSED, {
                    module: process.env.MODULE_NAME,
                    companyId
                });
                publish(Events.OPEN_MODULE, {
                    module: 'MODULE_SCHEDULEGENIE',
                    companyId,
                    additionalArgs: {
                        openedFromChatbot: true,
                        openedFromChatbotTopic: topic.name
                    }
                });
                break;
            case 'open_cm':
                publish(Events.MODULE_CLOSED, {
                    module: process.env.MODULE_NAME,
                    companyId: companyId
                });
                publish(Events.OPEN_MODULE, {
                    module: 'MODULE_CONCESSIONMANAGER',
                    companyId,
                    additionalArgs: {
                        offerId: parseInt(action),
                        openedFromChatbot: true,
                        openedFromChatbotTopic: topic.name
                    }
                });
                break;
            case 'response_text':
                setTimeout(() => {
                    showResponse(topic.action, `Here's the <b>${topic.display_name}</b> information`);
                    showAnotherTopicButton();
                }, typingDelay);
                break;
            case 'open_chat':
                openLiveChat(action, brandPrimaryColor);
                if(document.getElementById('custom-css')){
                    window.showChatWindow();
                }
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        startDialog();
    }, []);
    
    return chatBot ? (
        <>
            <Content showFooter={false}>
                <div id="clx-chatbot-dialog-window">
                    {history &&
                        history.map(entry => {
                            const Component = componentsMap[entry.type];
                            const content = entry.content || null;
                            return content ? (
                                <Component key={generateKey('content-component')}>{content}</Component>
                            ) : (
                                <Component key={generateKey('component')} />
                            );
                        })}
                </div>
            </Content>
        </>
    ) : (
        <></>
    );
};

export default Dialog;
